/**
 * battles system — v0M3.3 first pass + v0M3.4 engagement lock + v0M3.4a
 * post-battle resolution.
 *
 * Steps every active battle by BATTLES_ROUNDS_PER_TICK rounds per world
 * tick. When a battle transitions to `result !== null`:
 *   1. `resolveBattleConclusion` writes casualties back to campaign
 *      Cohorts, destroys zero-figure Cohorts, and disbands empty-roster
 *      Armies (see battle-conclusion.ts for the full contract).
 *   2. `releaseEngagementLock` clears `currentBattleId` on any surviving
 *      participating Armies (defensive — armies may have been disbanded
 *      in step 1).
 *   3. The battle is removed from `state.battles` — concluded battles
 *      have no further consequences and shouldn't accumulate.
 *
 * Runs the 5-phase engagement loop per BATTLES.md §2.7 through
 * `advanceBattleRound` from `./battle-round.ts`.
 *
 * Determinism: iterates battles by sortedIds. Each battle carries its
 * own PRNG (seeded at battle creation from a derivative of the world
 * seed + battle id), so battle order does not affect combat rolls. The
 * `sortedIds` snapshot is taken before the loop, so deleting from
 * `state.battles` during iteration is safe (we don't re-look-up
 * concluded battles).
 */

import type { Command } from './commands';
import type { GameState } from './state';
import type { RngState } from './rng';
import type { SimContext } from './sim-context';
import type { BattlePhase } from './battle-state';
import { advanceBattleSubPhase } from './battle-round';
import { resolveBattleConclusion } from './battle-conclusion';
import { sortedIds } from './state';
import { getDialNumber, loadDials } from '../data/dials';

// v0M4.1.3 — per-phase pacing (BATTLES.md §2.9.4). Retires the v0M4.0f
// single `round_pacing_ticks` dial. Each sub-phase runs for its own
// tick budget; battles.ts fires the phase logic + transitions when the
// current phase's dial elapses.
const BATTLE_DIALS = loadDials();
const PHASE_MOVEMENT_TICKS = Math.max(
  1,
  Math.round(getDialNumber(BATTLE_DIALS, 'battle_phase_movement_ticks')),
);
const PHASE_COMBAT_TICKS = Math.max(
  1,
  Math.round(getDialNumber(BATTLE_DIALS, 'battle_phase_combat_ticks')),
);
const PHASE_RESOLUTION_TICKS = Math.max(
  1,
  Math.round(getDialNumber(BATTLE_DIALS, 'battle_phase_resolution_ticks')),
);

function phaseDurationTicks(phase: BattlePhase): number {
  switch (phase) {
    case 'movement':   return PHASE_MOVEMENT_TICKS;
    case 'combat':     return PHASE_COMBAT_TICKS;
    case 'resolution': return PHASE_RESOLUTION_TICKS;
    case 'setup':      return 0; // Setup fires transition immediately when released.
  }
}

export function battlesSystem(
  state: GameState,
  _commands: readonly Command[],
  _rng: RngState,
  context: SimContext,
): GameState {
  for (const battleId of sortedIds(state.battles)) {
    const battle = state.battles.get(battleId)!;
    // v0M3.5 — Phase 1 (Setup) window: battle rounds are paused until
    // the deadline passes. MFix4.2: the Setup clock now runs from
    // battle creation (set in battle-factory.ts) so the window is
    // autonomous — no re-set on modal enter/exit.
    //
    // MFix4.2: a battle that is ALREADY concluded (e.g. instant end
    // via test fixture or a corner case) must still route through the
    // resolve/release/delete block below, even during the Setup
    // window. Only sub-phase advancement is gated.
    const inSetupWindow =
      battle.phase1EndTick !== null && state.tick < battle.phase1EndTick;
    if (!inSetupWindow && battle.phase1EndTick !== null && state.tick >= battle.phase1EndTick) {
      battle.phase1EndTick = null;
      battle.phaseStartTick = state.tick;
    }
    if (battle.result === null && !inSetupWindow) {
      // v0M4.1.3: per-phase pacing. When the current sub-phase's elapsed
      // ticks reach its dial threshold, fire the phase logic + transition.
      // A single tick may cross multiple thresholds if a phase's dial is
      // very small — we loop until either the elapsed budget is spent
      // or the battle concludes.
      let guard = 0;
      while (battle.result === null && guard < 4) {
        const elapsed = state.tick - battle.phaseStartTick;
        const duration = phaseDurationTicks(battle.currentPhase);
        if (elapsed < duration) break;
        advanceBattleSubPhase(battle, state.tick);
        guard++;
      }
    }
    // Concluded battle → resolve consequences, release lock, remove.
    // Ordering:
    //   resolve (writeback + destroy + disband + retreat)
    //   → release (idempotent; skips disbanded / landlocked armies)
    //   → delete from state.battles (prevents re-run next tick)
    if (battle.result !== null) {
      resolveBattleConclusion(state, battle, context);
      releaseEngagementLock(state, battle);
      state.battles.delete(battleId);
    }
  }
  return state;
}

/**
 * Clear `currentBattleId` on both Armies linked to a concluded battle,
 * unlocking them for the next tick's movement + ZoC checks.
 *
 * Missing Armies (e.g. army id removed by a scenario reset between
 * battle start and end) are silently skipped — this is defensive; the
 * v1 sim does not remove armies mid-battle.
 */
function releaseEngagementLock(
  state: GameState,
  battle: import('./battle-state').Battle,
): void {
  for (const armyId of battle.armyIds) {
    const army = state.armies.get(armyId);
    if (army === undefined) continue;
    army.currentBattleId = null;
  }
}
